import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';

/** A canned reason for disregarding a feedback item. */
export interface DisregardReasonOption {
  value: string;
  label: string;
}

/** The "Other" reason, which needs a typed explanation. */
export const DISREGARD_REASON_OTHER = 'other';

/**
 * Canned reasons for disregarding feedback — mostly the ways an AI review gets
 * something wrong (e.g. it flags a color, but the hex value checks out).
 */
export const DISREGARD_REASONS: readonly DisregardReasonOption[] = [
  { value: 'matches_mockup', label: 'Checked – it matches the mockup (AI misread it)' },
  { value: 'color_correct', label: 'Color / hex value is correct' },
  { value: 'size_correct', label: 'Spacing or size is correct when measured' },
  { value: 'typography_correct', label: 'Font / typography is correct' },
  { value: 'copy_correct', label: 'Copy matches the approved copy' },
  { value: 'screenshot_artifact', label: 'Screenshot artifact (banner, popup, animation, lazy load)' },
  { value: 'intentional', label: 'Intentional – differs from the mockup on purpose' },
  { value: 'client_approved', label: 'Client approved it as it is' },
  { value: 'out_of_scope', label: 'Out of scope for this build' },
  { value: 'duplicate', label: 'Duplicate of another item' },
  { value: DISREGARD_REASON_OTHER, label: 'Other (explain below)' },
];

/** What the user gave as the reason: a canned reason and an optional note. */
export interface DisregardReason {
  reason: string;
  reasonLabel: string;
  note: string;
}

/**
 * The comment text recording a disregard reason — one consistent shape so the
 * audit report (and, later, the AI reviewer) can read it:
 * "Disregarded — Color / hex value is correct. Note: checked #207F86."
 */
export function disregardReasonComment(disregardReason: DisregardReason): string {
  const reasonText = disregardReason.reason === DISREGARD_REASON_OTHER ? 'Other' : disregardReason.reasonLabel;
  const note = disregardReason.note.trim();
  return `Disregarded — ${reasonText}.` + (note ? ` Note: ${note}` : '');
}

/**
 * Asks why a feedback item is being disregarded before the status changes: pick a
 * canned reason (with an optional note), or "Other" with a typed explanation.
 * Presentational — the host changes the status and records the reason as a
 * comment on confirm, and reverts on cancel.
 *
 * @example
 * <fb-disregard-reason
 *   [open]="disregardPending() !== null"
 *   [itemLabel]="disregardPending()?.element"
 *   (confirm)="onDisregardConfirmed($event)"
 *   (cancel)="onDisregardCancelled()" />
 */
@Component({
  selector: 'fb-disregard-reason',
  templateUrl: './disregard-reason.html',
  styleUrl: './disregard-reason.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onCancel()' },
})
export class DisregardReasonDialog {
  /** Whether the dialog is shown. */
  readonly open = input<boolean>(false);

  /** The item being disregarded (e.g. its element), shown under the title. */
  readonly itemLabel = input<string | null | undefined>(null);

  /** Emitted with the reason when the user confirms. */
  readonly confirm = output<DisregardReason>();

  /** Emitted when the user backs out (Cancel, the overlay, or Escape). */
  readonly cancel = output<void>();

  protected readonly reasons = DISREGARD_REASONS;
  protected readonly selectedReason = signal<string>('');
  protected readonly note = signal('');

  protected readonly noteRequired = computed(() => this.selectedReason() === DISREGARD_REASON_OTHER);
  protected readonly canConfirm = computed(
    () => this.selectedReason() !== '' && (!this.noteRequired() || this.note().trim() !== ''),
  );

  constructor() {
    // Each time it opens, start from a blank answer.
    effect(() => {
      if (this.open()) {
        this.selectedReason.set('');
        this.note.set('');
      }
    });
  }

  protected onConfirm(): void {
    const chosenReason = this.reasons.find((reasonOption) => reasonOption.value === this.selectedReason());
    if (!this.canConfirm() || !chosenReason) {
      return;
    }
    this.confirm.emit({ reason: chosenReason.value, reasonLabel: chosenReason.label, note: this.note().trim() });
  }

  protected onCancel(): void {
    if (this.open()) {
      this.cancel.emit();
    }
  }
}
